/**
 * Client-side image compression for the web app.
 * Mirrors the mobile app's compressPhoto logic using Canvas API.
 *
 * - Resizes to max 1920px on the longest side
 * - Compresses as JPEG, targeting under 500 KB
 * - Progressively lowers quality if still too large
 */

const MAX_DIMENSION = 1920;
const MAX_BYTES = 500 * 1024; // 500 KB
const QUALITY_STEPS = [0.8, 0.6, 0.5, 0.4, 0.3];

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Canvas toBlob failed"))),
      "image/jpeg",
      quality,
    );
  });
}

/**
 * Compress an image File. Returns a new File (JPEG) under MAX_BYTES if possible.
 * Non-image files are returned as-is.
 * @param maxDimension Override the default max dimension (1920px). Use 512 for profile pictures.
 */
export async function compressImage(file: File, maxDimension = MAX_DIMENSION): Promise<File> {
  // Only compress images
  if (!file.type.startsWith("image/")) return file;

  // Small enough already — skip
  if (file.size <= MAX_BYTES) return file;

  const img = await loadImage(file);

  // Compute scaled dimensions
  let { width, height } = img;
  if (Math.max(width, height) > maxDimension) {
    if (width >= height) {
      height = Math.round((height * maxDimension) / width);
      width = maxDimension;
    } else {
      width = Math.round((width * maxDimension) / height);
      height = maxDimension;
    }
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, 0, 0, width, height);

  // Release object URL
  URL.revokeObjectURL(img.src);

  // Try each quality level until under target
  for (const quality of QUALITY_STEPS) {
    const blob = await canvasToBlob(canvas, quality);
    if (blob.size <= MAX_BYTES) {
      const name = file.name.replace(/\.\w+$/, ".jpg");
      return new File([blob], name, { type: "image/jpeg" });
    }
  }

  // Last resort: lowest quality result even if over target
  const blob = await canvasToBlob(canvas, QUALITY_STEPS[QUALITY_STEPS.length - 1]);
  const name = file.name.replace(/\.\w+$/, ".jpg");
  return new File([blob], name, { type: "image/jpeg" });
}

/**
 * Compress an array of Files in parallel.
 */
export async function compressImages(files: File[]): Promise<File[]> {
  return Promise.all(files.map(compressImage));
}
